import { Suspense, useId, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  DrawerBody,
  DrawerFooter,
  PageLoader,
  Paragraph,
  Tooltip,
} from '@customerio/pluma-components/react';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { useSidebarCompanion } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { DetailsPanel } from '../item/details-panel';
import { reachHref } from '../item/page-model';
import { useDrawerItem } from './use-drawer-item';
import { dataIndexItemPath, type DataIndexItemAddress } from '../urls';
import { getLabelForItem } from '../utils/item';
import { DrawerItemHeader } from './drawer-item-header';
import { EditDetails } from './edit-details';
import type { DrawerEditing } from './use-drawer-editing';
import { UsageAndActivity } from './usage-and-activity';

/**
 * A quick look at one row, without leaving the list.
 *
 * The same record the item page reads, shown as much of it as a side panel
 * can hold: the details, where it is used, and what has happened to it. The
 * page itself is one click away, and is where anything long-form happens.
 */

/** The width the page this replaces pins its own drawer to. */
const DRAWER_WIDTH = '550px';

type DataIndexDrawerProps = {
  workspaceId: number;
  /** The row being looked at, or nothing when the drawer is closed. */
  address: DataIndexItemAddress | null;
  onClose: () => void;
  /** Called once an attribute is gone, so the list can stop listing it. */
  onDeleted: (name: string) => void;
};

export function DataIndexDrawer({ workspaceId, address, onClose, onDeleted }: DataIndexDrawerProps) {
  // The last row looked at, kept while the panel slides out: clearing it with
  // the open state would empty the panel a frame before it has gone.
  const [showing, setShowing] = useState(address);
  // Which visit this is. The body holds the edit draft, and the same instance
  // kept across visits would carry a draft from the row looked at before —
  // saving it would write one row's description, tags and sensitivity onto
  // another. Remounting on the way in rather than on the way out, so the
  // panel never changes under the reader while it slides away.
  const [visit, setVisit] = useState(0);
  const [wasOpen, setWasOpen] = useState(address != null);
  // Copilot is a right-hand panel too, so the two share the edge: the drawer
  // sits to the left of an open sidebar and drops its overlay, rather than
  // covering a sidebar the reader is mid-conversation with. Claimed only
  // while a row is open — the panel stays mounted once closed, and a claim
  // that outlived it would restyle the sidebar for the whole list.
  const sidebar = useSidebarCompanion('copilot', address != null);
  const titleId = useId();

  if ((address != null) !== wasOpen) {
    setWasOpen(address != null);
  }

  if (address != null && (address !== showing || !wasOpen)) {
    setShowing(address);
    setVisit((current) => current + 1);
  }

  return (
    <Drawer
      isOpen={address != null}
      onClose={onClose}
      shouldShowCloseButton={false}
      // The panel's own width, which Pluma otherwise takes from its content:
      // the activity table is wider than the usage accordions, so the panel
      // doubled in width when that tab was selected. 550px is the width the
      // page this replaces pins, and its activity table scrolls inside it.
      style={{ ...sidebar.style, width: DRAWER_WIDTH }}
      withOverlay={sidebar.withOverlay}
      shouldCloseOnOverlayClick={sidebar.shouldCloseOnOverlayClick}
      // Named by the row's heading, which is a plain heading rather than
      // Pluma's drawer title so it can be the size the page this replaces
      // draws it at.
      aria-labelledby={titleId}
    >
      {showing == null ? null : (
        <>
          {/* Outside the boundaries, so the close button the drawer focuses
              as it opens is the one still there once the row has loaded. */}
          <DrawerItemHeader workspaceId={workspaceId} address={showing} titleId={titleId} />
          {/* The body suspends on the detail read, and a failed read would
              otherwise reach the layout's boundary and replace the list
              behind the panel. A row someone else has just deleted is the
              ordinary way in. Reset on the row, so opening another one tries
              again — by its path, which carries the object type as well as
              the name: the same name under another type is another row. */}
          <AppErrorBoundary
            resetKeys={[dataIndexItemPath(String(workspaceId), showing.kind, showing.name, showing.scope)]}
            fallbackRender={() => <DrawerReadFailed onClose={onClose} />}
          >
            <Suspense fallback={<DrawerSkeleton />}>
              <DataIndexDrawerBody
                key={visit}
                workspaceId={workspaceId}
                address={showing}
                onClose={onClose}
                onDeleted={onDeleted}
              />
            </Suspense>
          </AppErrorBoundary>
        </>
      )}
    </Drawer>
  );
}

type DrawerReadFailedProps = {
  onClose: () => void;
};

/** A read that failed. The header, and the close button in it, stay above it. */
function DrawerReadFailed({ onClose }: DrawerReadFailedProps) {
  return (
    <>
      <DrawerBody>
        <Paragraph>This item could not be loaded. It may have been deleted.</Paragraph>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="secondary" onClick={() => onClose()}>
          Cancel
        </Button>
      </DrawerFooter>
    </>
  );
}

function DrawerSkeleton() {
  return (
    <DrawerBody>
      <PageLoader label="Loading…" />
    </DrawerBody>
  );
}

type DataIndexDrawerBodyProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  onClose: () => void;
  onDeleted: (name: string) => void;
};

function DataIndexDrawerBody({ workspaceId, address, onClose, onDeleted }: DataIndexDrawerBodyProps) {
  const analytics = useAnalytics();
  const { item, tags, allTags, reach, model, writes, editing, canEdit } = useDrawerItem({
    workspaceId,
    address,
    onDeleted: () => {
      onClose();
      onDeleted(item.name);
    },
  });

  const itemHref = dataIndexItemPath(String(workspaceId), address.kind, item.name, address.scope);

  return (
    <>
      <DrawerBody>
        {editing.draft == null ? (
          <>
            <DetailsPanel
              layout="columns"
              item={item}
              kind={address.kind}
              tags={tags}
              usage={reach}
              reachHref={reachHref(workspaceId, address, reach.filters)}
              terms={model.reachTerms}
              reach={model.reach}
              campaignIndefinite={model.campaignIndefinite}
              header={{ onEdit: canEdit ? editing.start : undefined }}
              eventAttributes={{
                attributes: item.eventAttributes,
                allHref: `${itemHref}?tab=attributes`,
                isLeaving: true,
              }}
            />
            <Divider my="200" />
            <UsageAndActivity
              workspaceId={workspaceId}
              address={address}
              usage={item.usage}
              scope={model.usageScope}
              terms={model.usageTerms}
            />
          </>
        ) : (
          <EditDetails
            workspaceId={workspaceId}
            kind={address.kind}
            name={item.name}
            objectTypeId={address.scope.kind === 'profile' ? undefined : address.scope.objectTypeId}
            tags={allTags}
            draft={editing.draft}
            onChange={editing.change}
            canBeMarkedSensitive={model.canBeMarkedSensitive}
            canEdit={canEdit}
            isSaving={editing.isSaving}
            onNaming={editing.setIsNaming}
          />
        )}
      </DrawerBody>

      <DrawerFooter>
        <ButtonGroup groupVariant="spaced">
          {editing.draft == null ? (
            <ReadingActions
              canDelete={address.kind === 'attribute'}
              deleteBlockReason={model.deleteBlockReason}
              onDelete={() => void writes.confirmDelete()}
              onClose={onClose}
              itemHref={itemHref}
              onOpenFullPage={() => {
                void analytics.track('dataIndex', 'viewFullPageClicked', { type: getLabelForItem(address.kind) });
              }}
            />
          ) : (
            <EditingActions editing={editing} />
          )}
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
}

type EditingActionsProps = {
  editing: DrawerEditing;
};

/** What ends an edit. */
function EditingActions({ editing }: EditingActionsProps) {
  return (
    <>
      <Button variant="secondary" isDisabled={editing.isSaving} onClick={editing.cancel}>
        Cancel
      </Button>
      <Button
        variant="primary"
        isDisabled={editing.isNaming}
        isLoading={editing.isSaving}
        onClick={() => void editing.save()}
      >
        Save
      </Button>
    </>
  );
}

type ReadingActionsProps = {
  /** Only an attribute can be deleted, and never through an object's list. */
  canDelete: boolean;
  deleteBlockReason: string | undefined;
  onDelete: () => void;
  onClose: () => void;
  itemHref: string;
  onOpenFullPage: () => void;
};

/** What can be done to the row without editing it. */
function ReadingActions({
  canDelete,
  deleteBlockReason,
  onDelete,
  onClose,
  itemHref,
  onOpenFullPage,
}: ReadingActionsProps) {
  return (
    <>
      {canDelete ? (
        <Tooltip content={deleteBlockReason ?? ''} isCustom={true} isDisabled={deleteBlockReason == null}>
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              aria-label="Delete attribute"
              variant="tertiary"
              icon="delete-trash"
              isIconOnly
              isDisabled={deleteBlockReason != null}
              onClick={onDelete}
            />
          )}
        </Tooltip>
      ) : null}
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" href={itemHref} target="_blank" onClick={onOpenFullPage}>
        View full page
      </Button>
    </>
  );
}
